import { Stack, Typography } from "@mui/material";
import type { GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { Inbox } from "../../../../api/types/inboxesTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import { inboxChannelTypeCaptionsMap } from "../../../../utilities/enumCaptions";
import AgentAvatar from "./AgentAvatar";
import AgentInboxesContext from "./AgentInboxesContext";

const AgentsCell = (params: GridCellParams<Inbox>) => {
  const { agents } = useDefinedContext(AgentInboxesContext);

  const assignedAgentTypes = distinct(params.row.channels.flatMap((channel) => channel.dedicatedWorkflowAgentTypes));
  if (assignedAgentTypes.length === 0) {
    return "-";
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%", minWidth: 0 }}>
      <AgentAvatar size={24} />
      <InlineItemsList
        displayCount={1}
        items={assignedAgentTypes}
        propGetter={(agentType) => agents.find((a) => a.workflowAgentType === agentType)?.agentName || agentType}
      />
    </Stack>
  );
};

const NameCell = ({ row }: { row: Inbox }) => {
  const { clientCode } = useClientContext();

  return (
    <GridTextHoverLink
      to={`/${clientCode}/${pageRoutes.integration}/${pageRoutes.agenticWorkflows}/${pageRoutes.agentInboxes}/${row.id}`}
    >
      <Stack>
        <Typography noWrap>{row.name}</Typography>
        <Typography variant="caption" noWrap color="textSecondary">
          {row.description}
        </Typography>
      </Stack>
    </GridTextHoverLink>
  );
};

const columnDefinitions: GridColDef<Inbox>[] = [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => <NameCell row={row} />,
  },
  {
    field: "channels",
    headerName: "Channels",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) =>
      row.channels.length === 0 ? (
        "-"
      ) : (
        <InlineItemsList
          displayCount={1}
          items={row.channels}
          propGetter={(channel) => `${channel.name} (${inboxChannelTypeCaptionsMap[channel.type]})`}
        />
      ),
  },
  {
    field: "channels.dedicatedWorkflowAgentTypes",
    headerName: "Agents",
    flex: 1,
    minWidth: 160,
    renderCell: (params) => <AgentsCell {...params} />,
  },
];

interface Props {
  rows: Inbox[];
  loading?: boolean;
}

const AgentInboxesGrid = ({ rows, loading }: Props) => (
  <DataGrid
    rows={rows}
    columns={columnDefinitions}
    loading={loading}
    noRowsText="No inboxes"
    multilineRows
    disableColumnPinning
    disableColumnReorder
    disableColumnSorting
    disableMultipleRowSelection
  />
);

export default AgentInboxesGrid;
